/**
 * User-authored creatures. Each CustomCreature entry in a world spawns its
 * own sim instance — this class holds N instances of a single creature and
 * advances them each tick. Behaviour variants:
 *   - peaceful: random wander confined to a ring around the spawn centre
 *   - predator: damages any ant inside bite radius
 *   - prey:     flees the nearest ant in sense range
 *
 * Instances can be killed (ant swarms can chew down a predator over time);
 * dead instances respawn after a short delay.
 */

import type { Terrain } from '../game/Terrain';
import type { AntWorld } from './AntWorld';
import type { CustomCreature as CustomCreatureCfg } from '../ui/WorldStore';

export interface CustomInstance {
  alive: boolean;
  x: number; y: number; z: number;
  heading: number;
  speed: number;
  turnT: number;
  hp: number;
  respawnT: number;
  attacking: boolean;
}

const BITE_RADIUS = 0.22;
const BITE_RADIUS_SQ = BITE_RADIUS * BITE_RADIUS;
const ANT_BITE_ON_PREDATOR_DPS = 0.25;   // ants chew the predator back
const PREY_FLEE_RANGE = 3.5;
const PREY_FLEE_RANGE_SQ = PREY_FLEE_RANGE * PREY_FLEE_RANGE;

export class CustomCreatureSim {
  readonly instances: CustomInstance[] = [];
  cfg: CustomCreatureCfg;

  constructor(
    cfg: CustomCreatureCfg,
    private terrain: Terrain,
    private antWorld: AntWorld,
    private worldRadius: number,
  ) {
    this.cfg = cfg;
    for (let i = 0; i < Math.max(0, cfg.count); i++) {
      this.instances.push(this.spawnOne());
    }
  }

  private spawnOne(): CustomInstance {
    // Scatter randomly across the playable square.
    const r = Math.sqrt(Math.random()) * this.worldRadius * 0.85;
    const a = Math.random() * Math.PI * 2;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    return {
      alive: true,
      x, y: this.terrain.getHeight(x, z), z,
      heading: Math.random() * Math.PI * 2,
      speed: this.cfg.speed,
      turnT: 1 + Math.random() * 3,
      hp: this.cfg.hp,
      respawnT: 0,
      attacking: false,
    };
  }

  update(dt: number): void {
    const cfg = this.cfg;
    if (!cfg.enabled) return;
    for (const c of this.instances) {
      if (!c.alive) {
        c.respawnT -= dt;
        if (c.respawnT <= 0) Object.assign(c, this.spawnOne());
        continue;
      }

      // Decide where to go: flee nearest ant (prey), chase nearest ant
      // (predator), or idle-wander (peaceful).
      c.turnT -= dt;
      c.attacking = false;

      if (cfg.behavior === 'predator') {
        const target = nearestAnt(this.antWorld, c.x, c.z, 4);
        if (target) {
          c.heading = Math.atan2(target.z - c.z, target.x - c.x);
        } else if (c.turnT <= 0) {
          c.heading += (Math.random() - 0.5) * 1.4;
          c.turnT = 1 + Math.random() * 2;
        }
      } else if (cfg.behavior === 'prey') {
        const flee = nearestAnt(this.antWorld, c.x, c.z, PREY_FLEE_RANGE);
        if (flee) {
          c.heading = Math.atan2(c.z - flee.z, c.x - flee.x);
          c.turnT = 0.5;
        } else if (c.turnT <= 0) {
          c.heading += (Math.random() - 0.5) * 1.0;
          c.turnT = 1 + Math.random() * 2;
        }
      } else {
        // Peaceful — pure wander.
        if (c.turnT <= 0) {
          c.heading += (Math.random() - 0.5) * 0.8;
          c.turnT = 1.5 + Math.random() * 3;
        }
      }

      // Bias back toward origin if we're straying off the playable square.
      const rFromOrigin = Math.hypot(c.x, c.z);
      if (rFromOrigin > this.worldRadius * 0.95) {
        const inward = Math.atan2(-c.z, -c.x);
        c.heading = inward;
      }

      const sp = cfg.behavior === 'prey' ? cfg.speed * 1.4 : cfg.speed;
      c.x += Math.cos(c.heading) * sp * dt;
      c.z += Math.sin(c.heading) * sp * dt;
      c.y = this.terrain.getHeight(c.x, c.z);
      c.speed = sp;

      // Predator → damage ants in bite radius.
      if (cfg.behavior === 'predator' && cfg.bitePerSec > 0) {
        c.attacking = this.antWorld.damageArea(c.x, c.z, BITE_RADIUS, cfg.bitePerSec, dt) > 0;
      }

      // Swarm bite-back: count alive ants within BITE_RADIUS and take
      // damage from each. Same mechanic as the lizard so the ant hordes
      // can chew down oversized predators.
      if (cfg.hp > 0) {
        let attackers = 0;
        for (let i = 0; i < this.antWorld.ants.length; i++) {
          const a = this.antWorld.ants[i];
          if (!a.alive || a.nestCycleT > 0) continue;
          const dx = a.x - c.x;
          const dz = a.z - c.z;
          if (dx * dx + dz * dz <= BITE_RADIUS_SQ) attackers++;
        }
        if (attackers > 0) {
          c.hp -= attackers * ANT_BITE_ON_PREDATOR_DPS * dt;
          if (c.hp <= 0) {
            c.alive = false;
            c.hp = 0;
            c.respawnT = 20 + Math.random() * 40;  // 20–60 s respawn
          }
        }
      }
    }
    void PREY_FLEE_RANGE_SQ;  // referenced for potential distance-sq fastpath
  }

  /** Sync count with a config change — adds or removes instances to match. */
  syncCount(): void {
    const desired = Math.max(0, this.cfg.count);
    while (this.instances.length < desired) this.instances.push(this.spawnOne());
    while (this.instances.length > desired) this.instances.pop();
  }
}

function nearestAnt(
  world: AntWorld, x: number, z: number, maxR: number,
): { x: number; z: number } | null {
  const maxR2 = maxR * maxR;
  let bestD2 = maxR2;
  let best: { x: number; z: number } | null = null;
  for (let i = 0; i < world.ants.length; i++) {
    const a = world.ants[i];
    if (!a.alive || a.nestCycleT > 0 || a.role === 'player') continue;
    const dx = a.x - x, dz = a.z - z;
    const d2 = dx * dx + dz * dz;
    if (d2 < bestD2) { bestD2 = d2; best = { x: a.x, z: a.z }; }
  }
  return best;
}
